import { forwardRef, useMemo } from 'react'
import classNames from 'classnames'

import { Avatar, AvatarProps } from '../Avatar'

type SizeType = 'small' | 'normal' | 'big'

type Props = Omit<React.HTMLProps<HTMLDivElement>, 'children' | 'size' | 'onClick'> & {
  avatars: AvatarProps[]
  size?: SizeType
  onAddClick?: () => void
  numberOfUsers?: number
  showAvatarTooltip?: boolean
  disabled?: boolean
  align?: 'left' | 'right'
}

const groupSizeToAvatarSize: Record<SizeType, AvatarProps['size']> = {
  small: 'xs',
  normal: 's',
  big: 'm'
}

const sizeToPosition: Record<SizeType, number> = {
  small: 20,
  normal: 25,
  big: 30
}

const sizeToSizeStyle: Record<SizeType, string> = {
  small: 'w-[28px] h-[28px]',
  normal: 'w-[34px] h-[34px]',
  big: 'w-[42px] h-[42px]'
}

const sizeToFontSize: Record<SizeType, string> = {
  small: 'text-xs',
  normal: 'text-sm',
  big: 'text-base '
}

const sizeToHeight: Record<SizeType, string> = {
  small: 'h-7',
  normal: 'h-9',
  big: 'h-11'
}

/**
 * @group Components
 * @category Component
 */
export const AvatarGroup = forwardRef(
  (
    {
      className,
      onAddClick,
      size = 'normal',
      numberOfUsers = 10,
      avatars = [],
      disabled,
      showAvatarTooltip,
      align = 'left',
      ...restProps
    }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { displayedAvatars, hiddenAvatars, containerWidth } = useMemo(() => {
      const displayedAvatars = avatars.slice(0, numberOfUsers)
      const hiddenAvatars = avatars.slice(numberOfUsers)
      const containerWidth = (displayedAvatars.length + (hiddenAvatars.length > 0 ? 3 : 2)) * sizeToPosition[size]

      return { displayedAvatars, hiddenAvatars, containerWidth }
    }, [avatars, numberOfUsers, size])

    const avatarSize = groupSizeToAvatarSize[size]
    const buttonSize = sizeToSizeStyle[size]
    const fontSize = sizeToFontSize[size]
    const height = sizeToHeight[size]

    return (
      <div ref={ref} style={{ width: containerWidth }} className={classNames('flex w-fit', height)} {...restProps}>
        <div className={classNames('relative flex w-full items-center', className)}>
          {displayedAvatars.map((avatar, index) => {
            return (
              <div
                key={index}
                className="absolute rounded-full"
                style={{
                  left: align === 'left' ? index * sizeToPosition[size] : 'auto',
                  right: align === 'right' ? index * sizeToPosition[size] : 'auto'
                }}
              >
                <Avatar {...avatar} ref={undefined} size={avatarSize} showTooltip={showAvatarTooltip} />
              </div>
            )
          })}

          {hiddenAvatars.length > 0 && (
            <div
              style={{
                left: align === 'left' ? displayedAvatars.length * sizeToPosition[size] : 'auto',
                right: align === 'right' ? displayedAvatars.length * sizeToPosition[size] : 'auto'
              }}
              className={classNames(
                'absolute flex items-center justify-center rounded-full bg-primary-600 p-3',
                buttonSize
              )}
            >
              <p className={classNames('rounded-full text-center font-bold text-neutral-300', fontSize)}>
                +{hiddenAvatars.length}
              </p>
            </div>
          )}

          {onAddClick && (
            <div className="flex w-full items-center justify-end">
              <button
                className={classNames(
                  'peer right-0 flex items-center justify-center rounded-full border border-dashed bg-white',
                  !disabled && 'active:border-2 active:border-solid active:border-primary-600 active:text-neutral-300',
                  'disabled:bg-neutral-100 disabled:text-neutral-300',
                  buttonSize
                )}
                type="button"
                disabled={disabled}
                onClick={onAddClick}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="peer-hover:text-neutral-700"
                >
                  <path
                    d="M8.00065 3.33334V12.6667M3.33398 8.00001H12.6673"
                    stroke="currentColor"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }
)

AvatarGroup.displayName = 'AvatarGroup'
